<template>
    <section id="courses" class="welcome-courses" dir="rtl">
        <header class="welcome-courses__header">
            <div class="welcome-courses__header-main">
                <div class="welcome-courses__icon" aria-hidden="true">
                    <lucide-icon name="MonitorPlay" class="h-6 w-6" />
                </div>
                <div class="welcome-courses__header-text">
                    <span class="welcome-courses__eyebrow">محتوى تعليمي منظم</span>
                    <h2 class="welcome-courses__title">الدورات التعليمية</h2>
                    <p class="welcome-courses__subtitle">
                        اختر الدورة المناسبة لمرحلتك الدراسية وابدأ التعلم خطوة بخطوة مع محتوى عملي ومراجعات مستمرة.
                    </p>
                </div>
            </div>
            <router-link :to="{ name: 'courses' }" class="welcome-courses__cta">
                <span>عرض كل الدورات</span>
                <lucide-icon name="ArrowLeft" class="h-4 w-4" />
            </router-link>
        </header>

        <div v-if="loading" class="welcome-courses__grid">
            <article
                v-for="n in 4"
                :key="'skeleton-' + n"
                class="welcome-course-card welcome-course-card--skeleton"
            >
                <div class="welcome-course-thumb">
                    <div class="welcome-course-thumb__fill bg-brand-navy-10" />
                </div>
                <div class="welcome-course-card__body">
                    <div class="h-3 bg-brand-navy-10 rounded w-2/5" />
                    <div class="h-5 bg-brand-navy-10 rounded w-full mt-3" />
                    <div class="h-4 bg-brand-navy-10 rounded w-1/3 mt-4" />
                </div>
            </article>
        </div>

        <div v-else-if="!mappedCourses.length" class="welcome-courses__empty">
            <div class="welcome-courses__empty-icon" aria-hidden="true">
                <lucide-icon name="MonitorPlay" class="h-8 w-8" />
            </div>
            <p class="welcome-courses__empty-title">لا توجد دورات متاحة حالياً</p>
            <p class="welcome-courses__empty-text">جرّب لاحقاً أو انتقل إلى صفحة الدورات لاستكشاف المحتوى الكامل.</p>
            <router-link :to="{ name: 'courses' }" class="welcome-courses__cta welcome-courses__cta--center">
                <span>عرض كل الدورات</span>
                <lucide-icon name="ArrowLeft" class="h-4 w-4" />
            </router-link>
        </div>

        <div v-else class="welcome-courses__grid">
            <router-link
                v-for="c in mappedCourses"
                :key="c.id"
                :to="{ name: 'course', params: { id: c.id } }"
                tag="article"
                class="welcome-course-card group"
            >
                <div
                    class="welcome-course-thumb"
                    :class="c.useImage ? 'welcome-course-thumb--image' : `bg-gradient-to-br ${c.bg}`"
                >
                    <div
                        v-if="c.useImage"
                        class="welcome-course-thumb__fill welcome-course-thumb__image"
                        :style="{ backgroundImage: `url(${c.imageUrl})` }"
                        role="img"
                        :aria-label="c.name"
                    />
                    <template v-if="!c.useImage">
                        <div
                            :class="`absolute -top-1/3 -left-1/3 w-2/3 h-2/3 bg-gradient-to-br ${c.glow} blur-3xl opacity-70`"
                            :style="{ background: 'radial-gradient(circle, rgba(239,68,68,0.35), transparent 70%)' }"
                        />
                        <div class="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 blur-3xl opacity-60" :style="{ background: 'radial-gradient(circle, rgba(255,0,80,0.4), transparent 70%)' }" />

                        <div class="welcome-course-thumb__fill pointer-events-none flex items-start justify-end p-3">
                            <div class="flex items-center gap-1.5 bg-[rgba(0,0,0,0.4)] backdrop-blur-sm rounded-md px-2 py-1 border border-white-10">
                                <span class="h-4 w-4 rounded-full bg-[#FF0000] flex items-center justify-center">
                                    <lucide-icon name="PlayCircle" class="h-3 w-3 text-white" />
                                </span>
                                <span class="text-[10px] font-bold text-white tracking-wide">Coding<br>With Ghonaim</span>
                            </div>
                        </div>

                        <div class="welcome-course-thumb__fill flex flex-col items-center justify-center text-center px-4">
                            <div class="text-4xl md:text-5xl mb-1 opacity-90 drop-shadow-[0_0_15px_rgba(255,50,50,0.6)]" aria-hidden>
                                {{ c.overlayIcon }}
                            </div>
                            <h3 class="text-white font-black text-3xl md:text-4xl leading-tight tracking-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
                                {{ c.overlayTitle }}
                            </h3>
                            <h3
                                v-if="c.overlayLine2"
                                class="text-white font-black text-3xl md:text-4xl leading-tight tracking-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]"
                            >
                                {{ c.overlayLine2 }}
                            </h3>
                            <p
                                v-if="c.overlaySubtitle"
                                class="mt-1 text-[#FF4444] font-bold text-sm md:text-base drop-shadow-md"
                            >
                                {{ c.overlaySubtitle }}
                            </p>
                        </div>

                        <div class="welcome-course-thumb__fill pointer-events-none flex items-end">
                            <div class="w-full h-1 bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-80" />
                        </div>
                    </template>

                    <div
                        v-if="c.useImage"
                        class="welcome-course-thumb__fill welcome-course-thumb__hover pointer-events-none flex items-center justify-center"
                    >
                        <span class="welcome-course-thumb__play-btn">
                            <lucide-icon name="PlayCircle" class="h-8 w-8 text-white" />
                        </span>
                    </div>
                </div>

                <div class="welcome-course-card__body">
                    <div class="welcome-course-card__meta">
                        <span v-if="c.gradesText" class="welcome-course-card__badge welcome-course-card__badge--grade">
                            {{ c.gradesText }}
                        </span>
                        <span v-if="c.free" class="welcome-course-card__badge welcome-course-card__badge--free">
                            مجانية
                        </span>
                    </div>
                    <h3 class="welcome-course-title">{{ c.name }}</h3>
                    <div class="welcome-course-card__footer">
                        <span class="welcome-course-card__lectures">
                            <lucide-icon name="PlayCircle" class="h-3.5 w-3.5" />
                            {{ c.lecturesCount }} محاضرة
                        </span>
                        <span class="welcome-course-card__link">
                            عرض الدورة
                            <lucide-icon name="ArrowLeft" class="h-3.5 w-3.5" />
                        </span>
                    </div>
                </div>
            </router-link>
        </div>
    </section>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import LucideIcon from './LucideIcon.vue';
import { mapCoursesForLanding } from './landing-course-mapper';

export default {
    name: 'WelcomeQuickCourses',
    components: { LucideIcon },
    props: {
        courses: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...GlobalComputed,
        mappedCourses() {
            return mapCoursesForLanding(this.courses, { platform: this.platform });
        },
    },
};
</script>
